import type { DrawingDocument, Point2D } from '../model';
import type { EditorSelection } from './editor';
import { alignableObjectsInSelection } from './objectAlignment';
import { changeDepiction } from './drawingDocumentCore';

export type RotationAxis = 'x' | 'y' | 'z';

/** Orthographic depiction rotation, starting at z=0. Depth is retained in drawing units,
 * not inferred from wedge bonds or used as a chemical conformation. Rotate complete connected
 * structures so selecting one bond cannot tear a molecule apart. Never clamp individual atoms.
 */
export function rotateStructures3d(
  document: DrawingDocument,
  selection: EditorSelection,
  axis: RotationAxis,
  degrees: number,
  pivot?: Point2D
): DrawingDocument {
  if (!Number.isFinite(degrees) || degrees % 360 === 0) return document;
  const ids = alignableObjectsInSelection(document, selection).flatMap((object) =>
    object.kind === 'molecule' ? object.atomIds : []
  );
  if (ids.length < 2) return document;
  const source = document.depiction.coordinateSet;
  const points = ids.map((id) => ({ id, ...source.coordinates[id]!, z: source.depths?.[id] ?? 0 }));
  const center = {
    x: pivot?.x ?? points.reduce((sum, p) => sum + p.x, 0) / points.length,
    y: pivot?.y ?? points.reduce((sum, p) => sum + p.y, 0) / points.length,
    z: points.reduce((sum, p) => sum + p.z, 0) / points.length
  };
  const angle = (degrees * Math.PI) / 180;
  const c = Math.cos(angle),
    s = Math.sin(angle);
  const rotated = points.map((p) => {
    const x = p.x - center.x,
      y = p.y - center.y,
      z = p.z - center.z;
    const next =
      axis === 'x'
        ? { x, y: y * c - z * s, z: y * s + z * c }
        : axis === 'y'
          ? { x: x * c + z * s, y, z: -x * s + z * c }
          : { x: x * c - y * s, y: x * s + y * c, z };
    return { id: p.id, x: next.x + center.x, y: next.y + center.y, z: next.z + center.z };
  });
  // Out-of-page projections remain valid drawing coordinates, just like imported geometry.
  // Moving/clamping them here would change the pivot and make an inverse rotation lossy.
  const coordinates = { ...source.coordinates };
  const depths = { ...source.depths };
  const overrides = { ...document.depiction.bondLengthOverrides };
  const rotatedIds = new Set(ids);
  for (const p of rotated) {
    coordinates[p.id] = { x: p.x, y: p.y };
    depths[p.id] = p.z;
  }
  for (const edge of document.structure.graph.edges) {
    if (!rotatedIds.has(edge.source)) continue;
    const a = coordinates[edge.source]!,
      b = coordinates[edge.target]!;
    const beforeA = source.coordinates[edge.source]!,
      beforeB = source.coordinates[edge.target]!;
    if (Math.abs(Math.hypot(a.x - b.x, a.y - b.y) - Math.hypot(beforeA.x - beforeB.x, beforeA.y - beforeB.y)) > 0.1)
      overrides[edge.id] = true;
  }
  return changeDepiction(document, {
    coordinateSet: { ...source, coordinates, depths },
    bondLengthOverrides: overrides
  });
}
